import {useRouter} from 'next/router';
import {ReactNode, useCallback, useMemo} from 'react';
import {t} from '../../../../common/getTranslations';
import {requestContext} from '../../../../common/requestContext';
import {isTrue} from '../../../../common/shared/utils/isTrue';
import {twMerge} from '../../../../common/twMerge';
import {Divider} from '../../../../design-system/atoms/Divider/Divider';
import {StaticImage} from '../../../../design-system/atoms/StaticImage/StaticImage';
import {StaticImageType} from '../../../../design-system/atoms/StaticImage/StaticImageType.enum';
import {TabPositionControl} from '../../../../design-system/atoms/TabPositionControl/TabPositionControl';
import {Text} from '../../../../design-system/atoms/Text/Text';
import {CheckBox} from '../../../../design-system/molecules/CheckBox/CheckBox';
import {CheckBoxSize} from '../../../../design-system/molecules/CheckBox/CheckBoxSize.enum';
import {WARNING_REMAIN_DAYS} from '../../../../design-system/organisms/HistoryItem/constants';
import {HistoryItem} from '../../../../design-system/organisms/HistoryItem/HistoryItem';
import {MainTab} from '../../../../design-system/organisms/MainTab/MainTab';
import {MainTabItemType} from '../../../../design-system/organisms/MainTab/MainTabItemType.interface';
import {History, HistoryType} from '../../../../graphql/generated/types';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {ReactQueryInfiniteType} from '../../../../reactQuery/CodegenReactQueryType.type';
import {ReactQueryInfiniteFetcher} from '../../../../reactQuery/ReactQueryInfiniteFetcher';
import {theme} from '../../../../theme';
import {
  useInfiniteBuyTicketHistoryQuery,
  useInfiniteUseTicketHistoryQuery,
} from '../../../../wrapper/storeWrapper';
import {HistoryListType} from '../../type';

export function TicketHistoryContainer() {
  const router = useRouter();
  const currentTabType =
    router.query?.tab === HistoryListType.Use
      ? HistoryListType.Use
      : HistoryListType.Buy;
  const currentFreeOnly = isTrue(router.query?.is_free_only as string);

  const {isMobile, isWebView} = requestContext.requestInfo.display;

  const TICKET_TAB_ITEMS: MainTabItemType[] = useMemo(
    () => [
      {
        id: HistoryListType.Buy,
        text: '충전 내역',
        textTKey: 'more_ticket_history_tab_added_history',
        href: `/history/ticket?tab=${HistoryListType.Buy}&is_free_only=${currentFreeOnly}`,
      },
      {
        id: HistoryListType.Use,
        text: '사용 내역',
        textTKey: 'more_ticket_history_tab_used_history',
        href: `/history/ticket?tab=${HistoryListType.Use}&is_free_only=${currentFreeOnly}`,
      },
    ],
    [currentFreeOnly]
  );

  const handleClickFreeOnly = useCallback(() => {
    router.replace(
      `${
        window.location.pathname
      }?tab=${currentTabType}&is_free_only=${!currentFreeOnly}`
    );
  }, [currentFreeOnly, currentTabType, router]);

  const handleClickFreeOnlyBanner = useCallback(() => {
    if (!currentFreeOnly) {
      router.push(
        `${
          window.location.pathname
        }?tab=${currentTabType}&is_free_only=${!currentFreeOnly}`
      );
    }
  }, [currentFreeOnly, currentTabType, router]);

  const TicketHistoryFetcherComponent = ({
    children,
  }: {
    children: (historyList?: History[]) => ReactNode;
  }) =>
    currentTabType === HistoryListType.Buy ? (
      <ReactQueryInfiniteFetcher
        reactQuery={useInfiniteBuyTicketHistoryQuery}
        type={ReactQueryInfiniteType.InfinityScroll}
        variables={{
          page: 0,
          freeOnly: currentFreeOnly,
        }}
        options={{
          refetchOnMount: true,
          initialPageParam: {page: 0, freeOnly: currentFreeOnly},
          getNextPageParam: (lastPage, pages) => {
            return lastPage.buyTicketHistory?.isEnd
              ? undefined
              : {
                  page: pages.length,
                  freeOnly: currentFreeOnly,
                };
          },
        }}
      >
        {data =>
          children(
            data?.pages.reduce(
              (reducedHistoryList, page) =>
                reducedHistoryList.concat(page.buyTicketHistory?.list || []),
              [] as History[]
            )
          )
        }
      </ReactQueryInfiniteFetcher>
    ) : (
      <ReactQueryInfiniteFetcher
        reactQuery={useInfiniteUseTicketHistoryQuery}
        type={ReactQueryInfiniteType.InfinityScroll}
        variables={{
          page: 0,
          freeOnly: currentFreeOnly,
        }}
        options={{
          refetchOnMount: true,
          initialPageParam: {page: 0, freeOnly: currentFreeOnly},
          getNextPageParam: (lastPage, pages) => {
            return lastPage.useTicketHistory?.isEnd
              ? undefined
              : {
                  page: pages.length,
                  freeOnly: currentFreeOnly,
                };
          },
        }}
      >
        {data =>
          children(
            data?.pages.reduce(
              (reducedHistoryList, page) =>
                reducedHistoryList.concat(page.useTicketHistory?.list || []),
              [] as History[]
            )
          )
        }
      </ReactQueryInfiniteFetcher>
    );

  return (
    <div className="flex grow flex-col text-theme-solid-100">
      <TabPositionControl
        depth={1}
        isMobile={isMobile}
        isWebView={isWebView}
        disabled={!isMobile}
      >
        <MainTab
          showBorder
          isMobile={isMobile}
          title={t('more_ticket_history_title')}
          currentTabItemId={currentTabType}
          tabItems={TICKET_TAB_ITEMS}
          className={twMerge(!isMobile && 'px-15pxr')}
        />
      </TabPositionControl>

      {currentTabType === HistoryListType.Buy && (
        <div className="font-small2 mr-16pxr flex h-44pxr items-center justify-end text-theme-solid-100">
          <div
            className="flex"
            onClick={e => {
              LOG_CLICK_EVENT('선물무료_필터_클릭', e);
              handleClickFreeOnly();
            }}
            {...putTiaraObjectProps({
              click: {
                layer1: '서브탭',
                copy: currentFreeOnly ? 'OFF' : 'ON',
              },
            })}
          >
            <span className="cursor-pointer">
              {t('more_ticket_history_free_ticket_filter')}
            </span>
            <CheckBox
              className="ml-4pxr"
              checked={currentFreeOnly}
              size={CheckBoxSize.Small}
            />
          </div>
        </div>
      )}
      {currentTabType === HistoryListType.Buy && (
        <div
          className={twMerge(
            'font-small2 flex items-center bg-theme-bg-surface-alt py-13pxr px-15pxr',
            !currentFreeOnly && 'cursor-pointer'
          )}
          onClick={e => {
            LOG_CLICK_EVENT('선물무료_배너_클릭', e);
            handleClickFreeOnlyBanner();
          }}
          {...putTiaraObjectProps({
            click: {
              layer1: '서브탭',
            },
          })}
        >
          <StaticImage
            className="mr-10pxr h-38pxr w-36pxr"
            imageType={StaticImageType.CouponHistoryStatic}
          />
          <Text className="whitespace-pre-wrap">
            {t('more_ticket_history_free_ticket_filter_description')}
          </Text>
        </div>
      )}

      <TicketHistoryFetcherComponent>
        {(historyList?: History[]) =>
          historyList && historyList.length > 0 ? (
            historyList.map((history, idx) => (
              <div key={`${history.id}-${idx}`}>
                <HistoryItem
                  history={history}
                  hasLink={
                    history.historyType === HistoryType.CouponUsage ||
                    history.historyType === HistoryType.CouponReCharge
                  }
                  hasCreditMasterId={!!history.creditMasterId}
                  shouldWarnExpired={
                    (!!history.remainDays || history.remainDays === 0) &&
                    history.remainDays < WARNING_REMAIN_DAYS
                  }
                />
                <Divider
                  color={theme.colors['theme-transparent-10']}
                  className="mx-15pxr"
                />
              </div>
            ))
          ) : (
            <div className="my-101pxr mx-0 w-full text-center">
              <Text className="font-medium1 text-theme-solid-100">
                {currentTabType === HistoryListType.Buy
                  ? t('more_ticket_history_added_none')
                  : t('more_ticket_history_used_none')}
              </Text>
            </div>
          )
        }
      </TicketHistoryFetcherComponent>
    </div>
  );
}
